import type { BoggleSpecialTileKind } from '~/boggle/engine/specialTiles'
import type {
  BoggleChallengeSpecialTileChoice,
  BoggleChallengeSpecialTileCount,
  BoggleChallengeSpecialTileInteractionId,
  BoggleChallengeSpecialTileInteractionPresentation,
  BoggleChallengeSpecialTileMaximumCount,
  BoggleChallengeSpecialTileReturnRule,
  BoggleChallengeSpecialTileRuntimeHooks,
  BoggleChallengeSpecialTileSupplyId,
  BoggleChallengeSpecialTileSupplyPresentation,
  BoggleChallengeSpecialTileTag,
} from './challengeSpecialTileTypes'

type SpecialTilePartCommon = {
  excludeWhenEndIntervalOnSubmit?: boolean
  excludeWhenLettersDoNotRepopulate?: boolean
  excludesTags?: readonly BoggleChallengeSpecialTileTag[]
  tags?: readonly BoggleChallengeSpecialTileTag[]
}

type BoggleChallengeSpecialTileSupplyDefinition = SpecialTilePartCommon & {
  description: string
  label: string
  maximumCount: BoggleChallengeSpecialTileMaximumCount
  returnRule: BoggleChallengeSpecialTileReturnRule
  runtimeHooks?: BoggleChallengeSpecialTileRuntimeHooks
  startingCount: BoggleChallengeSpecialTileCount
  supplyPresentation: BoggleChallengeSpecialTileSupplyPresentation
}

type BoggleChallengeSpecialTileInteractionDefinition = SpecialTilePartCommon & {
  behaviorId: string
  description: string
  interactionPresentation: BoggleChallengeSpecialTileInteractionPresentation
  label: string
  runtimeHooks: BoggleChallengeSpecialTileRuntimeHooks
}

export const SUPPLY_DEFINITIONS: Record<
  BoggleChallengeSpecialTileSupplyId,
  BoggleChallengeSpecialTileSupplyDefinition
> = {
  'limited-one': {
    description: 'One tile, no return',
    label: 'One',
    maximumCount: 'match-start',
    returnRule: { type: 'none' },
    startingCount: 1,
    supplyPresentation: {
      icon: 'fixed',
      tooltipDescription: ['Board contains 1 tile at start', 'Does not respawn'],
    },
  },
  'limited-few': {
    description: 'A small fixed set, no return',
    label: 'Few',
    maximumCount: 'match-start',
    returnRule: { type: 'none' },
    startingCount: 3,
    supplyPresentation: {
      icon: 'fixed',
      tooltipDescription: ['Board contains 3 tiles at start', 'Does not respawn'],
    },
  },
  'limited-many': {
    description: 'A larger fixed set, no return',
    label: 'Many',
    maximumCount: 'match-start',
    returnRule: { type: 'none' },
    startingCount: 5,
    supplyPresentation: {
      icon: 'fixed',
      tooltipDescription: ['Board contains 5 tiles at start', 'Does not respawn'],
    },
  },
  'limited-all': {
    description: 'Every tile starts special',
    label: 'All',
    maximumCount: 'match-start',
    returnRule: { type: 'none' },
    runtimeHooks: {
      persistDuringBoardClears: true,
    },
    startingCount: 'all',
    supplyPresentation: {
      icon: 'fixed',
      tooltipDescription: ['Affects every tile at start', 'Does not respawn'],
    },
  },
  'reset-empty-one': {
    description: 'One tile; restores when the board empties',
    label: 'Restore One',
    maximumCount: 'match-start',
    returnRule: { type: 'reset-when-empty' },
    startingCount: 1,
    supplyPresentation: {
      icon: 'fixed',
      tooltipDescription: ['Board contains 1 tile at start', 'Count resets when none remain'],
    },
  },
  'reset-empty-few': {
    description: 'Two tiles; restores when emptied',
    label: 'Restore Few',
    maximumCount: 'match-start',
    returnRule: { type: 'reset-when-empty' },
    startingCount: 3,
    supplyPresentation: {
      icon: 'fixed',
      tooltipDescription: ['Board contains 3 tiles at start', 'Count resets when none remain'],
    },
  },
  'reset-empty-many': {
    description: 'Four tiles; restores when emptied',
    label: 'Restore Many',
    maximumCount: 'match-start',
    returnRule: { type: 'reset-when-empty' },
    startingCount: 5,
    supplyPresentation: {
      icon: 'fixed',
      tooltipDescription: ['Board contains 5 tiles at start', 'Count resets when none remain'],
    },
  },
  'reset-empty-all': {
    description: 'Every tile special; restores when emptied',
    label: 'Restore All',
    maximumCount: 'match-start',
    returnRule: { type: 'reset-when-empty' },
    runtimeHooks: {
      persistDuringBoardClears: true,
    },
    startingCount: 'all',
    supplyPresentation: {
      icon: 'fixed',
      tooltipDescription: ['Board is filled', 'Resets when none remain'],
    },
  },
  'regenerating-slow': {
    description: 'Rare return cadence',
    excludesTags: ['returning'],
    label: 'Slow',
    maximumCount: 1,
    returnRule: {
      everyXTiles: 8,
      type: 'spawn-cadence',
    },
    startingCount: 0,
    supplyPresentation: {
      icon: 'regenerating',
      tooltipDescription: ['Appears every 8 tiles'],
    },
    tags: ['returning'],
  },
  'regenerating-steady': {
    description: 'Steady return cadence',
    excludesTags: ['returning'],
    label: 'Steady',
    maximumCount: 2,
    returnRule: {
      everyXTiles: 5,
      type: 'spawn-cadence',
    },
    startingCount: 1,
    supplyPresentation: {
      icon: 'regenerating',
      tooltipDescription: ['Board contains 1 tile at start', 'Appears every 5 tiles'],
    },
    tags: ['returning'],
  },
  'regenerating-frequent': {
    description: 'Frequent return cadence',
    excludesTags: ['returning'],
    label: 'Frequent',
    maximumCount: 3,
    returnRule: {
      everyXTiles: 3,
      type: 'spawn-cadence',
    },
    startingCount: 3,
    supplyPresentation: {
      icon: 'regenerating',
      tooltipDescription: ['Board contains 3 tiles at start', 'Appears every 3 tiles'],
    },
    tags: ['returning'],
  },
  'regenerating-refill-one': {
    description: 'One tile refills after clears',
    excludeWhenEndIntervalOnSubmit: true,
    excludeWhenLettersDoNotRepopulate: true,
    excludesTags: ['returning'],
    label: 'Refill One',
    maximumCount: null,
    returnRule: {
      type: 'replace-when-cleared',
    },
    startingCount: 1,
    supplyPresentation: {
      icon: 'refill',
      tooltipDescription: ['Board contains 1 tile at start', 'Replaced after use'],
    },
    tags: ['returning'],
  },
  'regenerating-refill-two': {
    description: 'Two tiles refill after clears',
    excludeWhenEndIntervalOnSubmit: true,
    excludeWhenLettersDoNotRepopulate: true,
    excludesTags: ['returning'],
    label: 'Refill Two',
    maximumCount: null,
    returnRule: {
      type: 'replace-when-cleared',
    },
    startingCount: 2,
    supplyPresentation: {
      icon: 'refill',
      tooltipDescription: ['Board contains 2 tiles at start', 'Replaced after use'],
    },
    tags: ['returning'],
  },
}

export const INTERACTION_DEFINITIONS: Record<
  BoggleChallengeSpecialTileInteractionId,
  BoggleChallengeSpecialTileInteractionDefinition
> = {
  contagion: {
    behaviorId: 'contagion-standard',
    description: 'Spreads to all neighbors when played; disappears if ignored',
    excludeWhenEndIntervalOnSubmit: true,
    excludeWhenLettersDoNotRepopulate: true,
    interactionPresentation: {
      icon: 'spread-on-use',
      name: 'Contagion',
      tooltipDescription: [
        'When used, spreads to all adjacent tiles',
        'Disappears when ignored',
      ],
    },
    label: 'Contagion',
    runtimeHooks: {
      clearOnDisuse: true,
      persistDuringBoardClears: true,
      spreadToAllAdjacentOnUse: true,
    },
    tags: ['persistent'],
  },
  virus: {
    behaviorId: 'virus-standard',
    description: 'Disappears when played; spreads to one neighbor if ignored',
    excludeWhenEndIntervalOnSubmit: true,
    excludeWhenLettersDoNotRepopulate: true,
    interactionPresentation: {
      icon: 'spread-on-disuse',
      name: 'Virus',
      tooltipDescription: [
        'When ignored, spreads to 1 adjacent tile',
        'Disappears when used',
      ],
    },
    label: 'Virus',
    runtimeHooks: {
      clearOnUse: true,
      persistDuringBoardClears: true,
      spreadToOneAdjacentOnDisuse: true,
    },
    tags: ['persistent'],
  },
}

function uniqueTags(
  ...tagGroups: ReadonlyArray<readonly BoggleChallengeSpecialTileTag[] | undefined>
) {
  return Object.freeze(Array.from(new Set(tagGroups.flatMap(tags => tags ?? []))))
}

function mergeRuntimeHooks(
  supplyHooks: BoggleChallengeSpecialTileRuntimeHooks | undefined,
  interactionHooks: BoggleChallengeSpecialTileRuntimeHooks | undefined,
) {
  const merged = {
    ...supplyHooks,
    ...interactionHooks,
  }

  return Object.keys(merged).length > 0 ? Object.freeze(merged) : undefined
}

function freezeReturnRule(returnRule: BoggleChallengeSpecialTileReturnRule) {
  return Object.freeze({ ...returnRule })
}

function freezeSupplyPresentation(
  presentation: BoggleChallengeSpecialTileSupplyPresentation,
) {
  return Object.freeze({
    ...presentation,
    tooltipDescription: Object.freeze([...presentation.tooltipDescription]),
  })
}

function freezeInteractionPresentation(
  presentation: BoggleChallengeSpecialTileInteractionPresentation,
) {
  return Object.freeze({
    ...presentation,
    tooltipDescription: Object.freeze([...presentation.tooltipDescription]),
  })
}

export function specialTileDefinition({
  interactionId,
  kind,
  supplyId,
}: {
  interactionId: BoggleChallengeSpecialTileInteractionId | null
  kind: BoggleSpecialTileKind
  supplyId: BoggleChallengeSpecialTileSupplyId
}): BoggleChallengeSpecialTileChoice {
  const supply = SUPPLY_DEFINITIONS[supplyId]
  const interaction =
    interactionId === null ? null : INTERACTION_DEFINITIONS[interactionId]

  return Object.freeze({
    description: interaction?.description ?? supply.description,
    excludeWhenEndIntervalOnSubmit:
      supply.excludeWhenEndIntervalOnSubmit ||
      interaction?.excludeWhenEndIntervalOnSubmit ||
      undefined,
    excludeWhenLettersDoNotRepopulate:
      supply.excludeWhenLettersDoNotRepopulate ||
      interaction?.excludeWhenLettersDoNotRepopulate ||
      undefined,
    excludesTags: uniqueTags(supply.excludesTags, interaction?.excludesTags),
    id: interaction?.behaviorId ?? supplyId,
    interactionId,
    interactionPresentation: interaction
      ? freezeInteractionPresentation(interaction.interactionPresentation)
      : undefined,
    kind,
    label: interaction?.label ?? supply.label,
    maximumCount: supply.maximumCount,
    returnRule: freezeReturnRule(supply.returnRule),
    runtimeHooks: mergeRuntimeHooks(supply.runtimeHooks, interaction?.runtimeHooks),
    startingCount: supply.startingCount,
    supplyId,
    supplyPresentation: freezeSupplyPresentation(supply.supplyPresentation),
    tags: uniqueTags(supply.tags, interaction?.tags),
  })
}
